import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { selectiveRealtimeManager } from '@/lib/realtime';
import { useAuthStore } from '@/lib/auth';
import { usePageContext } from '@/components/layout/PageContextProvider';
import { Wifi, WifiOff, Activity } from 'lucide-react';

export function SubscriptionStatus() {
  const user = useAuthStore((state) => state.user);
  const { currentPage } = usePageContext();
  const [status, setStatus] = React.useState<any>(null);

  React.useEffect(() => {
    const updateStatus = () => {
      const subscriptionStatus = selectiveRealtimeManager.getSubscriptionStatus();
      setStatus(subscriptionStatus);
    };

    // Update status immediately
    updateStatus();

    // Update status every 5 seconds
    const interval = setInterval(updateStatus, 5000);

    return () => clearInterval(interval);
  }, [currentPage, user?.role]);

  if (!status) return null;

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Selective Subscriptions
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Status:</span>
          <Badge variant={status.isInitialized ? "default" : "destructive"} className="text-xs">
            {status.isInitialized ? (
              <>
                <Wifi className="h-3 w-3 mr-1" />
                Active
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3 mr-1" />
                Inactive
              </>
            )}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">User Role:</span>
          <Badge variant="outline" className="text-xs">
            {status.currentUserRole || 'None'}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Current Page:</span>
          <Badge variant="outline" className="text-xs">
            {status.currentPage || 'None'}
          </Badge>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Active Channels:</span>
          <Badge variant="outline" className="text-xs">
            {status.subscriptionCount}
          </Badge>
        </div>

        {status.activeSubscriptions.length > 0 && (
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Channels:</span>
            <div className="space-y-1">
              {status.activeSubscriptions.map((channel: string, index: number) => (
                <div key={index} className="text-xs bg-muted px-2 py-1 rounded">
                  {channel}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 